import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, X } from 'lucide-react';
import { PDF_PRESETS, PDF_PRESET_ORDER, DEFAULT_PRESET_ID } from '@/lib/pdf-presets';
import { cn } from '@/lib/utils';

/**
 * Phase 1 preset picker for PDF export.
 * Lightweight, portal-free overlay to sidestep Radix Dialog focus-trap issues
 * when this component is opened from within an existing modal-style view.
 */
export const PdfPresetDialog = ({ open, onOpenChange, onConfirm, defaultPresetId = DEFAULT_PRESET_ID }) => {
  const [selected, setSelected] = useState(defaultPresetId);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onOpenChange?.(false); };
    document.addEventListener('keydown', onKey);
    // Prevent body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const handleGenerate = () => {
    onConfirm?.(selected);
    onOpenChange?.(false);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      data-testid="pdf-preset-dialog"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/70"
        onClick={() => onOpenChange?.(false)}
        aria-hidden="true"
      />
      {/* Panel */}
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-indigo-100 p-2">
              <FileText className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Generate PDF Report</h2>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Choose the audience. Each preset controls which sections appear in the PDF. The on-screen report always shows the full detail.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-700"
            onClick={() => onOpenChange?.(false)}
            aria-label="Close"
            data-testid="pdf-preset-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {PDF_PRESET_ORDER.map(id => {
            const p = PDF_PRESETS[id];
            const isSelected = selected === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setSelected(id)}
                className={cn(
                  'w-full text-left flex items-start gap-3 rounded-xl border p-3 transition',
                  isSelected
                    ? 'border-indigo-400 bg-indigo-50/70 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                )}
                data-testid={`pdf-preset-option-${id}`}
              >
                <span
                  className={cn(
                    'mt-1 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center',
                    isSelected ? 'border-indigo-600' : 'border-slate-300'
                  )}
                >
                  {isSelected && <span className="h-2 w-2 rounded-full bg-indigo-600" />}
                </span>
                <span className="flex-1">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{p.label}</span>
                    <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-600" data-testid={`pdf-preset-pages-${id}`}>
                      ~{p.estimated_pages} pages
                    </Badge>
                  </span>
                  <span className="block text-xs text-slate-600 mt-1 leading-relaxed">{p.description}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="pdf-preset-cancel">
            Cancel
          </Button>
          <Button onClick={handleGenerate} data-testid="pdf-preset-generate">
            <Download className="mr-2 h-4 w-4" />
            Generate PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PdfPresetDialog;
